/**
 * Primeiro acesso do médico (PBI-04): aviso e aceite, captura, PIN, pronto.
 *
 * Chega-se aqui pelo convite depois do login por senha (decisão C3), então a
 * pessoa já está identificada e o `/enroll` sabe de quem é o vínculo.
 *
 * Três cuidados que a tela carrega:
 *
 * - recusar o aviso é legítimo: "Voltar" leva ao Hub sem registrar nada, e o
 *   médico continua entrando por senha;
 * - a captura é biometria. Fica numa ref, fora do estado do React, só até o
 *   envio, e some depois — nada de guardá-la "para mostrar de novo";
 * - o PIN é conferido na tela pela mesma regra do backend enquanto a pessoa
 *   digita, mas quem decide é o backend.
 *
 * Tudo acontece numa chamada só (`POST /identity/enroll`), no fim. Criar a
 * identidade na Neuro não tem desfazer, então nada é enviado antes de o
 * médico ter aceitado, capturado e confirmado o PIN.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import useFaceCapture, {
  CAPTURE_CAPTURING,
  CAPTURE_ERROR,
  CAPTURE_READY,
  CAPTURE_STARTING,
} from '../hooks/useFaceCapture';
import { enrollIdentity, identityErrorCode, pinRuleViolation } from '../services/identityService';
import { tenantNameToSlug } from '../utils/tenantSlug';

const STEPS = ['notice', 'capture', 'pin', 'done'];

// Recusa do enroll -> [passo para onde voltar, texto].
const ENROLL_FAILURE = {
  capture_failed: ['capture', 'identity.firstAccess.errors.captureFailed'],
  consent_required: ['notice', 'identity.firstAccess.errors.consentRequired'],
  invalid_pin: ['pin', 'identity.firstAccess.errors.invalidPin'],
  not_credentialed: ['pin', 'identity.firstAccess.errors.notCredentialed'],
  credential_unavailable: ['pin', 'identity.firstAccess.errors.credentialUnavailable'],
  already_enrolled: ['pin', 'identity.firstAccess.errors.alreadyEnrolled'],
  identity_already_bound: ['pin', 'identity.firstAccess.errors.alreadyEnrolled'],
};

// O enroll ainda responde algumas recusas só pelo status (503 e 502 do
// provisionamento). Sem código, o status decide.
function enrollFailure(error) {
  const code = identityErrorCode(error);
  if (ENROLL_FAILURE[code]) return ENROLL_FAILURE[code];
  const status = error && error.response && error.response.status;
  if (status === 422) return ENROLL_FAILURE.capture_failed;
  if (status === 503) return ENROLL_FAILURE.credential_unavailable;
  if (status === 409) return ENROLL_FAILURE.already_enrolled;
  if (status === 502) return ['pin', 'identity.firstAccess.errors.provisioning'];
  return ['pin', 'identity.firstAccess.errors.generic'];
}

const CAMERA_ERROR_KEY = {
  denied: 'auth.faceLogin.errors.denied',
  no_camera: 'auth.faceLogin.errors.noCamera',
  unsupported: 'auth.faceLogin.errors.unsupported',
};

export default function IdentityFirstAccessPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const camera = useFaceCapture();
  const [step, setStep] = useState('notice');
  const [consent, setConsent] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState(null);
  const captureRef = useRef(null);

  const { stop: stopCamera } = camera;

  // A captura some quando a pessoa sai da tela, concluída ou não.
  useEffect(() => () => { captureRef.current = null; }, []);

  // Destino depois de concluir: o mesmo do pop-up de confirmação — quem
  // trabalha em Saúde vai para Saúde › Visão geral.
  const goHome = useCallback(() => {
    const slug = user && user.tenant_name ? tenantNameToSlug(user.tenant_name) : null;
    const canSeeHealth = (user && user.permissions ? user.permissions : []).includes('health_care:read');
    if (slug && canSeeHealth) {
      navigate(`/${slug}/health/overview`, { replace: true });
      return;
    }
    navigate(slug ? `/${slug}` : '/dashboard', { replace: true });
  }, [navigate, user]);

  // Numa estação o login tem duas etapas, e o primeiro acesso é a segunda
  // para quem ainda não tem rosto: desistir aqui é desistir do login.
  const cancelLogin = useCallback(async () => {
    captureRef.current = null;
    stopCamera();
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate, stopCamera]);

  const takeCapture = async () => {
    setFailure(null);
    const blob = await camera.capture();
    if (!blob) return; // o hook foi para `error`, e a tela diz o porquê
    captureRef.current = blob;
    setCaptured(true);
    stopCamera();
  };

  const retake = () => {
    captureRef.current = null;
    setCaptured(false);
    camera.start();
  };

  const pinProblem = pinRuleViolation(pin);
  const pinFeedbackKey = (() => {
    if (!pin && !confirm) return 'identity.firstAccess.pin.hint';
    if (pinProblem) return `identity.firstAccess.pin.rules.${pinProblem}`;
    if (pin !== confirm) return 'identity.firstAccess.pin.rules.mismatch';
    return 'identity.firstAccess.pin.ready';
  })();
  const pinValid = !pinProblem && pin === confirm;

  const submit = async () => {
    setSubmitting(true);
    setFailure(null);
    try {
      await enrollIdentity({ pin, faceBlob: captureRef.current });
      captureRef.current = null;
      setPin('');
      setConfirm('');
      // `has_identity_binding` muda, e o convite no Hub precisa sumir.
      try { await refreshUser(); } catch { /* o convite some no próximo carregamento */ }
      setStep('done');
    } catch (error) {
      const [backTo, key] = enrollFailure(error);
      if (backTo === 'capture') {
        captureRef.current = null;
        setCaptured(false);
      }
      setFailure(t(key));
      setStep(backTo);
    } finally {
      setSubmitting(false);
    }
  };

  const captureCopy = (() => {
    if (camera.status === CAPTURE_ERROR) {
      return {
        icon: 'videocam_off',
        title: t('identity.firstAccess.capture.states.error.title'),
        body: t(CAMERA_ERROR_KEY[camera.errorKind] || 'identity.firstAccess.capture.states.error.body'),
      };
    }
    if (captured) return { icon: 'check_circle', key: 'captured' };
    if (camera.status === CAPTURE_CAPTURING) return { icon: 'photo_camera', key: 'capturing' };
    if (camera.status === CAPTURE_READY) return { icon: 'center_focus_strong', key: 'ready' };
    if (camera.status === CAPTURE_STARTING) return { icon: 'hourglass_top', key: 'starting' };
    return { icon: 'videocam_off', key: 'off' };
  })();
  const captureTitle = captureCopy.title || t(`identity.firstAccess.capture.states.${captureCopy.key}.title`);
  const captureBody = captureCopy.body || t(`identity.firstAccess.capture.states.${captureCopy.key}.body`);
  const showVideo = !captured && (camera.status === CAPTURE_READY || camera.status === CAPTURE_CAPTURING);

  const kicker = (key) => (
    <Typography
      component="span"
      sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
    >
      {t(key)}
    </Typography>
  );

  const actions = (children) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>{children}</Box>
  );

  return (
    <Box
      component="main"
      id="identityFirstAccessScreen"
      sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: { xs: 2, md: 4 }, bgcolor: 'background.default' }}
    >
      <Box sx={{ width: 'min(960px, 100%)', display: 'grid', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={theme.palette.mode === 'dark' ? '/images/wes-logo-white.svg' : '/images/logo_big.svg'}
            alt="WES Hub 360"
            sx={{ width: 132, height: 'auto' }}
          />
          <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>WES Identity</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
          <Box
            component="ol"
            aria-label={t('identity.firstAccess.stepsLabel')}
            sx={{ listStyle: 'none', m: 0, p: 0, display: { xs: 'flex', md: 'grid' }, gap: 1, flexWrap: 'wrap' }}
          >
            {STEPS.map((key, index) => {
              const active = key === step;
              return (
                <Box
                  component="li"
                  key={key}
                  aria-current={active ? 'step' : undefined}
                  data-identity-step-dot={key}
                  sx={(th) => ({
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: active ? 700 : 500,
                    color: active ? 'primary.main' : 'text.secondary',
                    bgcolor: active ? alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
                  })}
                >
                  {`${index + 1}. ${t(`identity.firstAccess.steps.${key}`)}`}
                </Box>
              );
            })}
          </Box>

          <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, display: 'grid', gap: 2, bgcolor: 'background.paper' }}>
            {failure ? <Alert severity="error" sx={{ borderRadius: 2 }}>{failure}</Alert> : null}

            {step === 'notice' ? (
              <Box data-identity-step="notice" sx={{ display: 'grid', gap: 2 }}>
                {kicker('identity.firstAccess.notice.kicker')}
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                  {t('identity.firstAccess.notice.title')}
                </Typography>
                <Typography color="text.secondary">{t('identity.firstAccess.notice.body')}</Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      id="identityConsentCheck"
                      checked={consent}
                      onChange={(event) => setConsent(event.target.checked)}
                    />
                  )}
                  label={t('identity.firstAccess.notice.consent')}
                />
                {actions(
                  <>
                    <Button variant="outlined" onClick={cancelLogin}>{t('identity.firstAccess.back')}</Button>
                    <Button
                      variant="contained"
                      id="identityConsentContinue"
                      disabled={!consent}
                      onClick={() => { setFailure(null); setStep('capture'); }}
                    >
                      {t('identity.firstAccess.continue')}
                    </Button>
                  </>,
                )}
              </Box>
            ) : null}

            {step === 'capture' ? (
              <Box data-identity-step="capture" sx={{ display: 'grid', gap: 2 }}>
                {kicker('identity.firstAccess.capture.kicker')}
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                  {t('identity.firstAccess.capture.title')}
                </Typography>
                <Box
                  id="identityCapturePreview"
                  sx={(th) => ({
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 240,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                    p: 3,
                    bgcolor: alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.12 : 0.06),
                  })}
                  aria-live="polite"
                >
                  <Box
                    component="video"
                    ref={camera.videoRef}
                    muted
                    playsInline
                    autoPlay
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)',
                      visibility: showVideo ? 'visible' : 'hidden',
                    }}
                  />
                  {!showVideo ? (
                    <Box sx={{ position: 'relative', display: 'grid', gap: 1, justifyItems: 'center', maxWidth: 420 }}>
                      <Box component="span" className="material-symbols-rounded" aria-hidden="true" sx={{ fontSize: 44, color: 'primary.main' }}>
                        {captureCopy.icon}
                      </Box>
                      <Typography component="strong" sx={{ fontWeight: 700 }} id="identityCaptureTitle">{captureTitle}</Typography>
                      <Typography variant="body2" color="text.secondary" id="identityCaptureDescription">{captureBody}</Typography>
                    </Box>
                  ) : null}
                </Box>
                {showVideo ? (
                  <Typography variant="body2" color="text.secondary">{captureBody}</Typography>
                ) : null}
                {actions(
                  <>
                    <Button variant="outlined" onClick={() => { stopCamera(); setStep('notice'); }}>
                      {t('identity.firstAccess.back')}
                    </Button>
                    {!captured ? (
                      <Button
                        variant="contained"
                        id="identityCaptureAction"
                        disabled={camera.status === CAPTURE_STARTING || camera.status === CAPTURE_CAPTURING}
                        onClick={camera.status === CAPTURE_READY ? takeCapture : () => camera.start()}
                      >
                        {camera.status === CAPTURE_READY
                          ? t('identity.firstAccess.capture.take')
                          : camera.status === CAPTURE_STARTING || camera.status === CAPTURE_CAPTURING
                            ? t('identity.firstAccess.capture.wait')
                            : t('identity.firstAccess.capture.enable')}
                      </Button>
                    ) : (
                      <Button variant="outlined" id="identityCaptureRetry" onClick={retake}>
                        {t('identity.firstAccess.capture.retake')}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      id="identityCaptureContinue"
                      disabled={!captured}
                      onClick={() => { setFailure(null); setStep('pin'); }}
                    >
                      {t('identity.firstAccess.capture.use')}
                    </Button>
                  </>,
                )}
              </Box>
            ) : null}

            {step === 'pin' ? (
              <Box data-identity-step="pin" sx={{ display: 'grid', gap: 2 }}>
                {kicker('identity.firstAccess.pin.kicker')}
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                  {t('identity.firstAccess.pin.title')}
                </Typography>
                <Typography color="text.secondary">{t('identity.firstAccess.pin.body')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    id="identityPinInput"
                    type="password"
                    label={t('identity.firstAccess.pin.pinLabel')}
                    value={pin}
                    onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                    inputProps={{ inputMode: 'numeric', maxLength: 8, autoComplete: 'new-password' }}
                    disabled={submitting}
                  />
                  <TextField
                    id="identityPinConfirmInput"
                    type="password"
                    label={t('identity.firstAccess.pin.confirmLabel')}
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value.replace(/\D/g, '').slice(0, 8))}
                    inputProps={{ inputMode: 'numeric', maxLength: 8, autoComplete: 'new-password' }}
                    disabled={submitting}
                  />
                </Box>
                <Typography
                  id="identityPinFeedback"
                  variant="body2"
                  role="status"
                  aria-live="polite"
                  sx={{ color: pinValid ? 'success.main' : 'text.secondary', fontWeight: pinValid ? 600 : 400 }}
                >
                  {t(pinFeedbackKey)}
                </Typography>
                {actions(
                  <>
                    <Button variant="outlined" onClick={() => setStep('capture')} disabled={submitting}>
                      {t('identity.firstAccess.back')}
                    </Button>
                    <Button variant="contained" id="identityPinContinue" disabled={!pinValid || submitting} onClick={submit}>
                      {submitting ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : t('identity.firstAccess.pin.create')}
                    </Button>
                  </>,
                )}
              </Box>
            ) : null}

            {step === 'done' ? (
              <Box data-identity-step="done" sx={{ display: 'grid', gap: 2 }}>
                {kicker('identity.firstAccess.done.kicker')}
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                  {t('identity.firstAccess.done.title')}
                </Typography>
                <Typography color="text.secondary">{t('identity.firstAccess.done.body')}</Typography>
                {actions(
                  <Button variant="contained" onClick={goHome}>{t('identity.firstAccess.done.goToHub')}</Button>,
                )}
              </Box>
            ) : null}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
